import { CSSProperties, FC, ReactNode } from "react";
import { Box, IconButton, TableCell, TableRow } from "@mui/material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Add } from "@mui/icons-material";
import { makeSxStyles } from "ui";

const DraggableTableRow: FC<{
  children: ReactNode;
  canAddRowOnHover?: boolean;
  id: string | number;
  style?: CSSProperties;
  activeRow: number | null;
  index: number;
  onAddClick?: (index: number) => void;
}> = ({
  children,
  canAddRowOnHover,
  id,
  style,
  activeRow,
  index,
  onAddClick,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: {
      type: "row",
    },
  });

  const styles = {
    ...style,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sxStyles = useStyles();

  return (
    <TableRow
      ref={setNodeRef}
      style={styles}
      hover
      selected={activeRow === index}
      sx={sxStyles.tableRow}
    >
      <TableCell
        component="th"
        scope="row"
        padding="none"
        sx={{ width: "48px" }}
      >
        <div {...attributes} {...listeners} style={{ position: "relative" }}>
          <Box
            sx={{
              cursor: "grab",
              display: "flex",
              alignItems: "center",
              padding: "0 12px",
              color: "white",
              "&:hover": {
                color: "grey.300",
              },
            }}
          >
            ⋮⋮
          </Box>
          {canAddRowOnHover && (
            <IconButton
              className="icon-button"
              sx={sxStyles.iconButton}
              onClick={() => onAddClick?.(index)}
            >
              <Add sx={sxStyles.icon} />
            </IconButton>
          )}
        </div>
      </TableCell>
      {children}
    </TableRow>
  );
};

const useStyles = makeSxStyles(({ palette: { primary } }) => ({
  tableRow: {
    "&:hover .icon-button": {
      display: "flex",
    },
    "&:hover": {
      boxShadow: `inset 0px -1px 0px 0px ${primary.main}, 0px 1px 0px 0px ${primary.main}`,
    },
    "&:hover .MuiTableCell-root": {
      borderBottomColor: `${primary.main} !important`,
    },
  },
  iconButton: {
    position: "absolute",
    display: "none",
    left: 0,
    marginTop: "18px",
    width: "16px",
    height: "16px",
    backgroundColor: primary.main,
    borderRadius: "50%",
    "&:hover": {
      backgroundColor: primary.main,
      opacity: "70%",
    },
  },
  icon: {
    width: "10px",
    height: "10px",
    fill: "black",
  },
}));

export default DraggableTableRow;
